/**
 * Smart Procurement (P0)
 *   • Today's Buy Brief (ranked, per supplier, cash-aware)
 *   • Cash / Account posture
 *   • Reorder-point auto-tune (preview + apply)
 *   • Per-product & per-supplier overrides
 *   • Optional AI narrative (Claude Sonnet 5 via Emergent LLM key)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Sparkles, RefreshCw, Settings2, Wand2, AlertTriangle, CheckCircle2,
  Wallet, TrendingUp, Ban, Pencil, ChevronDown, ChevronRight, Loader2,
  MessageSquare, ArrowUp, ArrowDown, Pause,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const POSTURE_BADGE = {
  COD_OK: { className: "bg-emerald-100 text-emerald-800 border border-emerald-200", label: "COD — pay cash" },
  COD_TIGHT: { className: "bg-amber-100 text-amber-800 border border-amber-200", label: "COD — cash tight" },
  ACCOUNT_OK: { className: "bg-blue-100 text-blue-800 border border-blue-200", label: "On account" },
  ACCOUNT_OVER_LIMIT: { className: "bg-red-100 text-red-800 border border-red-200", label: "Over credit limit" },
};

export default function SmartProcurement() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // supplier_id -> bool
  const [narrative, setNarrative] = useState("");
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [globalCfg, setGlobalCfg] = useState(null);
  const [productOverrideOpen, setProductOverrideOpen] = useState(null); // holds line obj
  const [productOverride, setProductOverride] = useState({});
  const [supplierOverrideOpen, setSupplierOverrideOpen] = useState(null); // holds supplier obj
  const [supplierOverride, setSupplierOverride] = useState({});
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [instructionText, setInstructionText] = useState("");
  const [instructionBusy, setInstructionBusy] = useState(false);
  const [instructionPreview, setInstructionPreview] = useState(null);
  const [autoTuneRes, setAutoTuneRes] = useState(null);
  const [autoTuneBusy, setAutoTuneBusy] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);

  const fetchBrief = useCallback(async () => {
    setLoading(true);
    setNarrative("");
    try {
      const r = await axios.get(`${API}/smart-procurement/brief`);
      setBrief(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load smart buy brief");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGlobal = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/smart-procurement/overrides`);
      const g = r.data?.global?.global || {};
      setGlobalCfg({
        cash_safety_buffer: g.cash_safety_buffer ?? 15000,
        default_lead_time_cod: g.default_lead_time_cod ?? 3,
        default_lead_time_account: g.default_lead_time_account ?? 7,
        default_safety_days: g.default_safety_days ?? 3,
        target_stock_days: g.target_stock_days ?? 30,
        velocity_window_days: g.velocity_window_days ?? 90,
      });
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  const openGlobal = () => { if (!globalCfg) fetchGlobal(); setGlobalOpen(true); };

  const saveGlobal = async () => {
    try {
      await axios.put(`${API}/smart-procurement/overrides/global`, {
        ...globalCfg,
        cash_safety_buffer: Number(globalCfg.cash_safety_buffer) || 0,
        default_lead_time_cod: Number(globalCfg.default_lead_time_cod) || 3,
        default_lead_time_account: Number(globalCfg.default_lead_time_account) || 7,
        default_safety_days: Number(globalCfg.default_safety_days) || 3,
        target_stock_days: Number(globalCfg.target_stock_days) || 30,
        velocity_window_days: Number(globalCfg.velocity_window_days) || 90,
      });
      toast.success("Global tuning saved");
      setGlobalOpen(false);
      fetchBrief();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const openProductOverride = async (line) => {
    setProductOverrideOpen(line);
    setProductOverride({});
    try {
      const r = await axios.get(`${API}/smart-procurement/overrides`);
      const existing = r.data?.product?.[line.product_id] || {};
      setProductOverride({
        manual_daily_velocity: existing.manual_daily_velocity ?? "",
        manual_lead_time_days: existing.manual_lead_time_days ?? "",
        safety_days: existing.safety_days ?? "",
        exclude_from_smart_buy: existing.exclude_from_smart_buy ?? false,
        force_include: existing.force_include ?? false,
        notes: existing.notes ?? "",
      });
    } catch { /* silent */ }
  };

  const saveProductOverride = async () => {
    if (!productOverrideOpen) return;
    setSavingOverride(true);
    try {
      const payload = {};
      ["manual_daily_velocity", "manual_lead_time_days", "safety_days"].forEach(k => {
        if (productOverride[k] !== "" && productOverride[k] != null) {
          payload[k] = Number(productOverride[k]);
        }
      });
      if (productOverride.exclude_from_smart_buy) payload.exclude_from_smart_buy = true;
      if (productOverride.force_include) payload.force_include = true;
      if (productOverride.notes) payload.notes = productOverride.notes;
      await axios.put(
        `${API}/smart-procurement/overrides/product/${productOverrideOpen.product_id}`,
        payload,
      );
      toast.success("Override saved");
      setProductOverrideOpen(null);
      fetchBrief();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingOverride(false);
    }
  };

  const clearProductOverride = async () => {
    if (!productOverrideOpen) return;
    setSavingOverride(true);
    try {
      await axios.delete(`${API}/smart-procurement/overrides/product/${productOverrideOpen.product_id}`);
      toast.success("Override cleared");
      setProductOverrideOpen(null);
      fetchBrief();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingOverride(false);
    }
  };

  const openSupplierOverride = async (sup) => {
    setSupplierOverrideOpen(sup);
    setSupplierOverride({});
    try {
      const r = await axios.get(`${API}/smart-procurement/overrides`);
      const existing = r.data?.supplier?.[sup.supplier_id] || {};
      setSupplierOverride({
        lead_time_days: existing.lead_time_days ?? "",
        credit_limit: existing.credit_limit ?? "",
        priority: existing.priority || "normal",
        pause_until: existing.pause_until || "",
        exclude_from_smart_buy: existing.exclude_from_smart_buy ?? false,
        notes: existing.notes ?? "",
      });
    } catch { /* silent */ }
  };

  const saveSupplierOverride = async () => {
    if (!supplierOverrideOpen) return;
    setSavingOverride(true);
    try {
      const payload = {};
      if (supplierOverride.lead_time_days !== "" && supplierOverride.lead_time_days != null) {
        payload.lead_time_days = Number(supplierOverride.lead_time_days);
      }
      if (supplierOverride.credit_limit !== "" && supplierOverride.credit_limit != null) {
        payload.credit_limit = Number(supplierOverride.credit_limit);
      }
      if (supplierOverride.priority && supplierOverride.priority !== "normal") {
        payload.priority = supplierOverride.priority;
      }
      if (supplierOverride.pause_until) payload.pause_until = supplierOverride.pause_until;
      if (supplierOverride.exclude_from_smart_buy) payload.exclude_from_smart_buy = true;
      if (supplierOverride.notes) payload.notes = supplierOverride.notes;
      await axios.put(
        `${API}/smart-procurement/overrides/supplier/${supplierOverrideOpen.supplier_id}`,
        payload,
      );
      toast.success(`Saved for ${supplierOverrideOpen.supplier_name}`);
      setSupplierOverrideOpen(null);
      fetchBrief();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingOverride(false);
    }
  };

  const clearSupplierOverride = async () => {
    if (!supplierOverrideOpen) return;
    setSavingOverride(true);
    try {
      await axios.delete(`${API}/smart-procurement/overrides/supplier/${supplierOverrideOpen.supplier_id}`);
      toast.success("Supplier override cleared");
      setSupplierOverrideOpen(null);
      fetchBrief();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingOverride(false);
    }
  };

  const askAssistant = async () => {
    const text = (instructionText || "").trim();
    if (!text) return;
    setInstructionBusy(true);
    setInstructionPreview(null);
    try {
      const r = await axios.post(`${API}/smart-procurement/apply-instruction`, { instruction: text });
      setInstructionPreview(r.data);
      if (r.data.error) toast.warning(r.data.error);
      else if (!r.data.actions?.length) toast.info("The assistant couldn't map that to a change. Try rephrasing.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assistant failed");
    } finally {
      setInstructionBusy(false);
    }
  };

  const applyInstructionActions = async () => {
    const actions = instructionPreview?.actions || [];
    if (!actions.length) return;
    setInstructionBusy(true);
    let ok = 0;
    for (const a of actions) {
      try {
        if (a.scope === "global") {
          await axios.put(`${API}/smart-procurement/overrides/global`, a.changes || {});
        } else if (a.scope === "supplier") {
          await axios.put(`${API}/smart-procurement/overrides/supplier/${a.ref_id}`, a.changes || {});
        } else if (a.scope === "product") {
          await axios.put(`${API}/smart-procurement/overrides/product/${a.ref_id}`, a.changes || {});
        }
        ok += 1;
      } catch {
        // Continue applying — surface partial-fail below.
      }
    }
    setInstructionBusy(false);
    toast.success(`Applied ${ok} of ${actions.length} change${actions.length === 1 ? "" : "s"}`);
    setInstructionOpen(false);
    setInstructionText("");
    setInstructionPreview(null);
    fetchBrief();
  };

  const runAutoTune = async (apply) => {
    setAutoTuneBusy(true);
    try {
      const r = await axios.post(
        `${API}/smart-procurement/auto-tune`,
        {},
        { params: { apply } },
      );
      setAutoTuneRes(r.data);
      if (apply) {
        toast.success(`Applied ${r.data.applied_count} reorder-level updates`);
        fetchBrief();
      } else {
        toast.info(`${r.data.changed_count} products would be updated`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-tune failed");
    } finally {
      setAutoTuneBusy(false);
    }
  };

  const askAI = async () => {
    setNarrativeBusy(true);
    setNarrative("");
    try {
      const r = await axios.post(`${API}/smart-procurement/ai-narrative`);
      setNarrative(r.data?.narrative || "(empty response)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI narrative failed");
    } finally {
      setNarrativeBusy(false);
    }
  };

  const cashPosture = brief?.cash_posture;
  const suppliers = brief?.suppliers || [];

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="smart-procurement-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-lg p-2">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Smart Procurement</h1>
            <p className="text-sm text-slate-500">Today's buy brief — cash-aware, per supplier, editable</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchBrief} disabled={loading} data-testid="sp-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={openGlobal} data-testid="sp-tuning">
            <Settings2 className="h-4 w-4 mr-1" /> Tuning
          </Button>
          <Button variant="outline" size="sm" onClick={() => setInstructionOpen(true)} data-testid="sp-tell-btn">
            <MessageSquare className="h-4 w-4 mr-1" /> Tell the system
          </Button>
          <Button variant="outline" size="sm" onClick={askAI} disabled={narrativeBusy} data-testid="sp-ai">
            {narrativeBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
            Ask AI
          </Button>
        </div>
      </div>

      <Tabs defaultValue="brief">
        <TabsList>
          <TabsTrigger value="brief" data-testid="sp-tab-brief">Buy Brief</TabsTrigger>
          <TabsTrigger value="autotune" data-testid="sp-tab-autotune">Auto-Tune Reorder Points</TabsTrigger>
        </TabsList>

        <TabsContent value="brief" className="space-y-4">
          {/* Cash posture strip */}
          {cashPosture && (
            <Card>
              <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-4">
                <PostureBox icon={<Wallet className="h-4 w-4" />} label="Bank" value={fmt(cashPosture.bank_balance)} testid="sp-bank" />
                <PostureBox label="Safety buffer" value={fmt(cashPosture.safety_buffer)} muted />
                <PostureBox label="Cash for COD" value={fmt(cashPosture.cash_available_for_cod)} accent="emerald" />
                <PostureBox label="Planned COD" value={fmt(cashPosture.planned_cod_spend)} accent="amber" />
                <PostureBox label="Planned account" value={fmt(cashPosture.planned_account_spend)} accent="blue" />
                <PostureBox label="Total planned" value={fmt(cashPosture.planned_total_spend)} accent="indigo" bold />
              </CardContent>
              {(cashPosture.skipped_cod_due_to_cash > 0 || cashPosture.skipped_over_credit > 0) && (
                <CardContent className="px-4 pb-4 pt-0 text-sm text-amber-800 bg-amber-50 border-t">
                  <AlertTriangle className="inline h-4 w-4 mr-1" />
                  Deferred: {fmt(cashPosture.skipped_cod_due_to_cash)} COD (cash tight) + {fmt(cashPosture.skipped_over_credit)} (over credit limit).
                  Consider paying down before ordering more.
                </CardContent>
              )}
            </Card>
          )}

          {/* AI narrative */}
          {narrative && (
            <Card>
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-2.5">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wand2 className="h-4 w-4" /> AI Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 whitespace-pre-wrap text-sm leading-relaxed" data-testid="sp-narrative">
                {narrative}
              </CardContent>
            </Card>
          )}

          {loading ? (
            <p className="text-center py-10 text-slate-500">Crunching the numbers…</p>
          ) : suppliers.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
              Nothing to buy today — every product is above its reorder threshold.
            </CardContent></Card>
          ) : (
            suppliers.map(sup => (
              <SupplierGroup
                key={sup.supplier_id}
                sup={sup}
                expanded={!!expanded[sup.supplier_id]}
                toggle={() => setExpanded(s => ({ ...s, [sup.supplier_id]: !s[sup.supplier_id] }))}
                onEditLine={openProductOverride}
                onEditSupplier={openSupplierOverride}
              />
            ))
          )}

          {brief?.trimmed > 0 && (
            <p className="text-xs text-center text-slate-500">
              (+{brief.trimmed} smaller suppliers hidden — increase "Max suppliers in brief" under Tuning to see them.)
            </p>
          )}
        </TabsContent>

        <TabsContent value="autotune" className="space-y-4">
          <Card>
            <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> Reorder-Point Auto-Tune
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm text-slate-600">
                Recomputes each product's <strong>Min / Reorder / Max</strong> from real 90-day
                velocity + supplier lead time. Preview first, then apply — you can always
                override per product afterwards.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => runAutoTune(false)} disabled={autoTuneBusy} data-testid="sp-preview-tune">
                  {autoTuneBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Preview changes
                </Button>
                <Button size="sm" onClick={() => runAutoTune(true)} disabled={autoTuneBusy || !autoTuneRes || autoTuneRes.changed_count === 0} data-testid="sp-apply-tune">
                  Apply {autoTuneRes ? `(${autoTuneRes.changed_count})` : ""}
                </Button>
              </div>
              {autoTuneRes && autoTuneRes.changes.length > 0 && (
                <div className="border rounded overflow-hidden max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">PLU</th>
                        <th className="text-left px-2 py-1.5">Product</th>
                        <th className="text-left px-2 py-1.5">Supplier</th>
                        <th className="text-right px-2 py-1.5">Velocity / day</th>
                        <th className="text-right px-2 py-1.5">Min</th>
                        <th className="text-right px-2 py-1.5">Reorder</th>
                        <th className="text-right px-2 py-1.5">Max</th>
                      </tr>
                    </thead>
                    <tbody data-testid="sp-tune-changes">
                      {autoTuneRes.changes.slice(0, 200).map(c => (
                        <tr key={c.product_id} className="border-t">
                          <td className="px-2 py-1 font-mono text-xs">{c.plu_code}</td>
                          <td className="px-2 py-1">{c.name}</td>
                          <td className="px-2 py-1 text-slate-500">{c.supplier_name}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{c.daily_velocity}</td>
                          <td className="px-2 py-1 text-right tabular-nums"><Diff a={c.old.min} b={c.new.min} /></td>
                          <td className="px-2 py-1 text-right tabular-nums"><Diff a={c.old.reorder} b={c.new.reorder} /></td>
                          <td className="px-2 py-1 text-right tabular-nums"><Diff a={c.old.max} b={c.new.max} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tuning dialog */}
      <Dialog open={globalOpen} onOpenChange={setGlobalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Global Procurement Tuning</DialogTitle></DialogHeader>
          {!globalCfg ? <p>Loading…</p> : (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <TuningInput label="Cash safety buffer (R)" value={globalCfg.cash_safety_buffer}
                onChange={v => setGlobalCfg({ ...globalCfg, cash_safety_buffer: v })} testid="sp-cfg-buffer" />
              <TuningInput label="Target stock days" value={globalCfg.target_stock_days}
                onChange={v => setGlobalCfg({ ...globalCfg, target_stock_days: v })} testid="sp-cfg-target" />
              <TuningInput label="Default lead time — COD (days)" value={globalCfg.default_lead_time_cod}
                onChange={v => setGlobalCfg({ ...globalCfg, default_lead_time_cod: v })} testid="sp-cfg-cod" />
              <TuningInput label="Default lead time — account (days)" value={globalCfg.default_lead_time_account}
                onChange={v => setGlobalCfg({ ...globalCfg, default_lead_time_account: v })} testid="sp-cfg-acct" />
              <TuningInput label="Default safety days" value={globalCfg.default_safety_days}
                onChange={v => setGlobalCfg({ ...globalCfg, default_safety_days: v })} testid="sp-cfg-safety" />
              <TuningInput label="Velocity window (days)" value={globalCfg.velocity_window_days}
                onChange={v => setGlobalCfg({ ...globalCfg, velocity_window_days: v })} testid="sp-cfg-window" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGlobalOpen(false)}>Cancel</Button>
            <Button onClick={saveGlobal} data-testid="sp-cfg-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-product override dialog */}
      <Dialog open={!!productOverrideOpen} onOpenChange={() => setProductOverrideOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{productOverrideOpen?.name}</DialogTitle>
            <p className="text-xs text-slate-500">
              PLU {productOverrideOpen?.plu_code} • {productOverrideOpen?.supplier_name}
            </p>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <TuningInput label="Override daily velocity" value={productOverride.manual_daily_velocity}
              onChange={v => setProductOverride({ ...productOverride, manual_daily_velocity: v })}
              placeholder={String(productOverrideOpen?.daily_velocity)} testid="sp-po-velocity" />
            <TuningInput label="Override lead time (days)" value={productOverride.manual_lead_time_days}
              onChange={v => setProductOverride({ ...productOverride, manual_lead_time_days: v })}
              placeholder={String(productOverrideOpen?.lead_time_days)} testid="sp-po-lead" />
            <TuningInput label="Extra safety days" value={productOverride.safety_days}
              onChange={v => setProductOverride({ ...productOverride, safety_days: v })}
              placeholder={String(productOverrideOpen?.safety_days)} testid="sp-po-safety" />
            <div className="col-span-2 space-y-2 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!productOverride.exclude_from_smart_buy}
                  onChange={e => setProductOverride({ ...productOverride, exclude_from_smart_buy: e.target.checked })}
                  data-testid="sp-po-exclude" />
                Never suggest this product (I know something the system doesn't)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!productOverride.force_include}
                  onChange={e => setProductOverride({ ...productOverride, force_include: e.target.checked })}
                  data-testid="sp-po-force" />
                Always include (even without recent sales history)
              </label>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} value={productOverride.notes || ""}
                  onChange={e => setProductOverride({ ...productOverride, notes: e.target.value })}
                  placeholder="Why? (e.g. seasonal, one-off promo, discontinued)" />
              </div>
            </div>
          </div>
          <DialogFooter className="justify-between">
            <Button variant="ghost" onClick={clearProductOverride} disabled={savingOverride}
              className="text-red-600" data-testid="sp-po-clear">
              <Ban className="h-4 w-4 mr-1" /> Clear override
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setProductOverrideOpen(null)}>Cancel</Button>
              <Button onClick={saveProductOverride} disabled={savingOverride} data-testid="sp-po-save">
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SupplierGroup = ({ sup, expanded, toggle, onEditLine }) => {
  const badge = POSTURE_BADGE[sup.posture] || POSTURE_BADGE.ACCOUNT_OK;
  return (
    <Card>
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition"
        data-testid={`sp-supplier-${sup.supplier_id}`}
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{sup.supplier_name}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${badge.className}`}>{badge.label}</span>
            <span className="text-xs text-slate-500">
              {sup.line_count} line{sup.line_count === 1 ? "" : "s"} • Owed {fmt(sup.current_balance)}
              {sup.credit_limit > 0 && ` / limit ${fmt(sup.credit_limit)}`}
            </span>
          </div>
          {!expanded && sup.posture_reason && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-1">{sup.posture_reason}</p>
          )}
        </div>
        <div className="text-right tabular-nums font-bold">{fmt(sup.est_total)}</div>
      </button>
      {expanded && (
        <CardContent className="p-0 border-t">
          {sup.posture_reason && (
            <div className="px-4 py-2 text-sm bg-slate-50 border-b text-slate-700">
              {sup.posture_reason}
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-3 py-1.5">PLU</th>
                <th className="text-left px-3 py-1.5">Product</th>
                <th className="text-right px-3 py-1.5">Stock</th>
                <th className="text-right px-3 py-1.5">Days cover</th>
                <th className="text-right px-3 py-1.5">Suggest Qty</th>
                <th className="text-right px-3 py-1.5">Est Cost</th>
                <th className="text-left px-3 py-1.5">Reason</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sup.lines.map(l => (
                <tr key={l.product_id} className="border-t hover:bg-blue-50">
                  <td className="px-3 py-1.5 font-mono text-xs">{l.plu_code}</td>
                  <td className="px-3 py-1.5">{l.name}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{l.current_stock}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {l.days_of_cover == null ? "—" : `${l.days_of_cover}d`}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{l.suggested_qty}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(l.est_line_cost)}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{l.reason}</td>
                  <td className="px-3 py-1.5">
                    <button
                      onClick={() => onEditLine(l)}
                      className="text-slate-400 hover:text-indigo-600"
                      title="Override this suggestion"
                      data-testid={`sp-line-edit-${l.product_id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      )}
    </Card>
  );
};

const PostureBox = ({ icon, label, value, muted, accent, bold, testid }) => {
  const colorMap = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    blue: "text-blue-700",
    indigo: "text-indigo-700",
  };
  return (
    <div className="text-center">
      <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
        {icon} {label}
      </p>
      <p
        className={`text-lg tabular-nums ${bold ? "font-bold" : "font-semibold"} ${muted ? "text-slate-400" : colorMap[accent] || "text-slate-900"}`}
        data-testid={testid}
      >
        {value}
      </p>
    </div>
  );
};

const TuningInput = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input
      type="number" step="0.01"
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
    />
  </div>
);

const Diff = ({ a, b }) => {
  const changed = a !== b;
  return (
    <span className={changed ? "text-indigo-700 font-semibold" : "text-slate-500"}>
      {a === b ? a : `${a} → ${b}`}
    </span>
  );
};
